import { useCallback } from "react";
import { ClipboardDocumentIcon } from "@heroicons/react/24/outline";

import { Button } from "shared/ui/shadcn/button";
import { clsxMerge } from "shared/lib/helpers";
import { schedulingLinkSupportsGuests } from "shared/lib/helpers/scheduling";
import { toast } from "shared/ui/shadcn/sonner";

import { useFetchLookalike } from "@/modules/pipeline/account-details/data/queries";

interface LookalikeGuestBlockPropsI {
  campaignId: string | undefined;
  // The booking URL the SDR opens. Used only to decide whether the provider
  // pre-adds the guest via the link (Calendly / cal.com / Zcal) or the SDR has
  // to paste it in by hand.
  schedulingLink: string | null | undefined;
  className?: string;
}

/**
 * Shows the SDR their lookalike guest email with a copy button, so the calendar
 * invite reaches the lookalike inbox and the ICS pipeline can sync the meeting.
 *
 * Shared by BOTH booking flows: the account-page scheduling-link modal
 * (InstructionsStep) and the in-call dialer Calendly modal. Renders nothing
 * unless the campaign has a verified lookalike address, so it is safe to drop
 * into any booking surface unconditionally.
 */
export const LookalikeGuestBlock = ({
  campaignId,
  schedulingLink,
  className,
}: LookalikeGuestBlockPropsI) => {
  const { data: lookalike } = useFetchLookalike(campaignId);

  // Calendly / cal.com / Zcal pre-add the guest through the link itself; other
  // providers can't, so there the SDR still has to add it by hand.
  const guestAutoEmbedded = schedulingLinkSupportsGuests(schedulingLink);

  const handleCopyLookalike = useCallback(() => {
    if (lookalike?.email) {
      navigator.clipboard.writeText(lookalike.email);
      toast.success("Email copied to clipboard");
    }
  }, [lookalike?.email]);

  // Non-lookalike campaigns (no address, or domain not SES-verified) show nothing.
  if (!lookalike?.email || !lookalike.is_domain_verified) {
    return null;
  }

  return (
    <div
      data-testid="lookalike-copy-block"
      className={clsxMerge(
        "rounded-lg border border-base-300 bg-base-200 p-3",
        className
      )}
    >
      <p className="mb-1 typography-body-5-medium">
        {guestAutoEmbedded
          ? "Added as a guest automatically. If it's missing on the page, add it:"
          : "Add this email as a guest:"}
      </p>
      <div className="flex items-center gap-2">
        <span
          data-testid="lookalike-email"
          className="flex-1 break-all font-mono typography-body-4"
        >
          {lookalike.email}
        </span>
        <Button
          data-testid="lookalike-copy-button"
          variant="ghost"
          size="sm"
          aria-label="Copy lookalike email"
          onClick={handleCopyLookalike}
        >
          <ClipboardDocumentIcon className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
